import { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { LoaderCircle, Search } from 'lucide-react';

import { FeedCard } from '@/components/feed/FeedCard';
import { FeedItemReveal } from '@/components/feed/FeedItemReveal';
import { PhraseChipRow, usePhraseChips, type ChipState, type ChipStates } from '@/components/PhraseChips';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useCardFeed } from '@/hooks/useCardFeed';
import type { PhraseChip, PhraseTokenDto } from '@/services';

const ease = [0.22, 1, 0.36, 1] as const;

function FeedSkeletonRow({ animated }: { animated: boolean }) {
  return (
    <div className="border-b border-slate-100 py-4 last:border-b-0">
      <div className="flex items-center gap-2.5">
        <Skeleton animated={animated} className="size-[30px] shrink-0" />
        <div className="min-w-0 flex-1 space-y-1.5">
          <Skeleton animated={animated} className="h-2.5 w-[34%]" />
          <Skeleton animated={false} className="h-2 w-[20%] bg-slate-50" />
        </div>
      </div>
      <div className="mt-2.5 space-y-2 border-l-2 border-[#f4ebd8] pl-3">
        <Skeleton animated={animated} className="h-[11px] w-[96%]" />
        <Skeleton animated={animated} className="h-[11px] w-[88%]" />
        <Skeleton animated={animated} className="h-[11px] w-[54%]" />
      </div>
      <Skeleton animated={false} className="mt-3 h-3 w-[46%] bg-slate-50" />
    </div>
  );
}

/**
 * Only the top two rows shimmer; the rest fade toward static placeholders. Seven pulsing rows on a
 * tall panel read as noise — a couple of live blocks are enough to say where the first result lands.
 */
function FeedLoadingSkeleton() {
  return (
    <div
      aria-hidden="true"
      style={{
        maskImage: 'linear-gradient(180deg, #000 0%, #000 42%, rgba(0,0,0,.35) 74%, transparent 100%)',
        WebkitMaskImage:
          'linear-gradient(180deg, #000 0%, #000 42%, rgba(0,0,0,.35) 74%, transparent 100%)',
      }}
    >
      {[0, 1, 2, 3, 4].map((index) => (
        <FeedSkeletonRow key={index} animated={index < 2} />
      ))}
    </div>
  );
}

function FeedEmptyState({ atDefault }: { atDefault: boolean }) {
  return (
    <div className="flex h-full flex-col">
      {/* Centered in the space above the tip below, rather than pinned to the top of a tall panel
          with a dead gap under it. */}
      <div className="flex flex-1 flex-col items-center justify-center px-6 pb-6 pt-4 text-center">
        <span className="flex size-[54px] items-center justify-center rounded-full bg-slate-100 text-slate-500">
          <Search className="size-6" strokeWidth={1.8} />
        </span>
        <p className="mt-3.5 font-semibold text-slate-900">No live examples found</p>
        <p className="mt-1.5 max-w-[280px] text-sm leading-6 text-slate-500">
          {atDefault
            ? 'This exact phrase doesn’t turn up in recent articles right now.'
            : 'Nothing matches the selected words.'}
        </p>
      </div>

      {/* Pinned to the bottom of the panel rather than trailing right under the message, so it
          reads as the panel's standing advice, not an afterthought squeezed under the empty note. */}
      <div className="border-t border-slate-100 pt-4 text-left">
        <p className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-slate-400">
          What can be done
        </p>
        <p className="mt-2 text-[13px] leading-6 text-slate-600">
          Loosen the filter above — release a word’s exact form or switch a chip off to widen the
          search.
        </p>
      </div>
    </div>
  );
}

interface CardFeedPanelProps {
  meaningId: string;
  open: boolean;
  panelId?: string;
  initialChips?: readonly PhraseChip[];
  phraseTokens?: readonly PhraseTokenDto[];
  chipStates?: ChipStates;
  onChipChange?: (lemma: string, state: ChipState) => void;
}

export function CardFeedPanel({
  meaningId,
  open,
  panelId = 'card-feed-panel',
  initialChips,
  phraseTokens,
  chipStates: controlledChipStates,
  onChipChange: controlledOnChipChange,
}: CardFeedPanelProps) {
  const duration = 0.28;
  // Kept short on purpose: with one slot the matches cannot start rising until the state they
  // replace has cleared out, so this delay is paid before every arrival.
  const handoff = 0.18;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const ownChipState = usePhraseChips(meaningId);
  const chipStates = controlledChipStates ?? ownChipState.chipStates;
  const setChipState = controlledOnChipChange ?? ownChipState.setChipState;

  const liveFeed = useCardFeed(meaningId, {
    enabled: open,
    initialChips,
    phraseTokens,
    chipStates,
    checkSense: true,
  });
  const activeFeed = liveFeed;
  const items = activeFeed.items;
  const chips = liveFeed.chips;
  const atDefault = liveFeed.atDefault;
  const { loading, loadingMore, hasMore, error, loadMore, retry } = activeFeed;

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const loadMoreTrigger = loadMoreTriggerRef.current;

    if (
      !open ||
      loading ||
      loadingMore ||
      !hasMore ||
      error ||
      !scrollContainer ||
      !loadMoreTrigger
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          void loadMore();
        }
      },
      {
        root: scrollContainer,
        rootMargin: '0px 0px 160px',
      }
    );

    observer.observe(loadMoreTrigger);
    return () => observer.disconnect();
  }, [error, hasMore, loadMore, loading, loadingMore, open]);

  if (!open) return null;

  return (
    <div id={panelId} className="flex h-full min-h-0 flex-col">
      <div className="shrink-0">
        <p id={`${panelId}-description`} className="sr-only">
          Where this card has appeared in recently published articles. Each chip carries a word and
          the part of speech it is held to; relax either, or switch the chip off, to search more
          widely and see all matching sentences.
        </p>
        {chips.length > 0 && (
          <div className="border-b border-[#f1eee8] px-[18px] py-3">
            <PhraseChipRow
              chips={chips}
              states={chipStates}
              onChipChange={setChipState}
              label="Filter live matches"
            />
          </div>
        )}
      </div>

      <div ref={scrollContainerRef} className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
        {/* popLayout, not wait: with "wait" the skeleton only gets to mount once the previous
            list's exit animation finishes — and a live search on a small corpus routinely resolves
            faster than that exit, so `loading` would already be back to false by the time the
            skeleton's turn came up and it would never appear at all. popLayout lets it mount the
            instant `loading` flips true, in parallel with whatever is leaving. */}
        <AnimatePresence mode="popLayout" initial={false}>
          {loading ? (
            <motion.div
              key="feed-loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, transition: { duration: handoff } }}
              transition={{ duration }}
            >
              <FeedLoadingSkeleton />
            </motion.div>
          ) : items.length > 0 ? (
            <motion.div
              key="feed-list"
              // The list itself does not fade in — its matches carry their own entrance.
              initial={false}
              exit={{ opacity: 0, transition: { duration: handoff } }}
              className="divide-y divide-slate-100"
            >
              <AnimatePresence>
                {items.map((item, index) => (
                  <FeedItemReveal
                    key={item.matchId}
                    index={index}
                    className="py-4 first:pt-0 last:pb-0"
                  >
                    <FeedCard item={item} />
                  </FeedItemReveal>
                ))}
              </AnimatePresence>
            </motion.div>
          ) : !error && !hasMore ? (
            /* hasMore also guards the search: an empty id window resumes below that
               window instead of being mistaken for the end of the corpus. */
            <motion.div
              key="feed-empty"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98, transition: { duration: handoff } }}
              transition={{ duration, ease }}
              className="h-full"
            >
              <FeedEmptyState atDefault={atDefault} />
            </motion.div>
          ) : null}
        </AnimatePresence>

        {error && (
          <div role="alert" className="flex flex-col gap-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between">
            <span>{error}</span>
            <Button
              type="button"
              variant="outline"
              onClick={retry}
              className="h-8 shrink-0 rounded-full border-red-200 bg-white text-red-700 hover:bg-red-100"
            >
              Try again
            </Button>
          </div>
        )}

        {!loading && hasMore && (
          <div ref={loadMoreTriggerRef}>
            <Button
              type="button"
              variant="outline"
              onClick={loadMore}
              disabled={loadingMore}
              className="h-10 w-full rounded-full border-slate-200 bg-white text-sm text-slate-800 hover:bg-slate-50"
            >
              {loadingMore ? (
                <>
                  <LoaderCircle className="size-4 animate-spin" />
                  Loading...
                </>
              ) : (
                'Load more'
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
